import styled from 'styled-components';

import { sectionColor } from '../../../lib/theme';

// Shared visual atoms for the parent account page cards: bulleted credential
// lists with star-icon defaults, and per-contact override rows.

export const CredentialList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
`;

export const CredentialRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
`;

export const CredentialValue = styled.span`
  font-weight: 500;
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const DefaultTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.56);
  background: ${sectionColor};
  padding: 2px 8px;
  border-radius: 999px;
`;

// Player-contact override rows. Collapsed (default) renders as a tight
// single-line summary with an Edit affordance; expanded renders fields +
// action buttons. Stacks below 600px so labels don't collide on phones.
export const ContactRow = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;

  &:first-child {
    margin-top: 0;
  }
`;

// Collapsed summary. Desktop: tight single line with inline separators between
// player / association / credential, IconButton vertically centred on the right.
// Mobile: label stacks each field on its own line, IconButton anchors top-right
// (align-items: flex-start) so it never wraps to its own orphaned row.
export const ContactSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 599px) {
    align-items: flex-start;
  }
`;

export const ContactSummaryLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;

  @media (max-width: 599px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
  }
`;

export const ContactPlayer = styled.span`
  font-weight: 600;

  @media (max-width: 599px) {
    font-size: 15px;
  }
`;

// Desktop renders the association name inline preceded by " — ". On mobile the
// label stacks vertically so the dash is meaningless — hide it and let the row
// stand on its own line. The dash is a CSS ::before so we don't have to feed it
// through JSX (and so the mobile rule is a one-liner).
export const ContactCompany = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.56);

  &:before {
    content: ' — ';
    color: rgba(0, 0, 0, 0.32);
  }

  @media (max-width: 599px) {
    display: block;

    &:before {
      content: '';
    }
  }
`;

export const ContactCredential = styled.span`
  color: rgba(0, 0, 0, 0.7);

  &:before {
    content: '·';
    margin: 0 6px;
    color: rgba(0, 0, 0, 0.32);
  }

  @media (max-width: 599px) {
    display: block;
    font-size: 13px;
    overflow-wrap: anywhere;

    &:before {
      content: '';
      margin: 0;
    }
  }
`;

export const ContactEditPanel = styled.div`
  padding-top: 12px;
`;

export const ContactFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 599px) {
    grid-template-columns: 1fr;
  }
`;

// Action row layout depends on JSX order: Save (1), Apply default (2),
// Cancel (3), Delete (4). Desktop keeps the flat wrap (Delete shoves to the
// right via marginLeft:auto on the JSX). Mobile switches to a 2-col grid:
// Save and Apply default span both columns (full-width, affirm actions);
// Cancel and Delete share the final row (cancel left, destructive right).
export const ContactActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;

  @media (max-width: 599px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    > .MuiButton-root:nth-child(-n+2) {
      grid-column: 1 / -1;
    }
    /* The desktop layout pushes Delete right via inline marginLeft:auto;
       neutralise it here so the grid placement isn't shifted. */
    > .MuiButton-root {
      margin-left: 0 !important;
    }
  }
`;

// Expandable wrapper for the whole per-player-contacts list. Collapsed by
// default — the parent opens it only when they want to make changes.
export const ContactsExpanderHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 0;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: inherit;

  &:hover {
    color: rgba(0, 0, 0, 0.72);
  }
`;
